import { useEffect } from "react";
import Lenis from "@studio-freight/lenis";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { setScrollProgress, scrollState, onPhaseChange, hasSeenIntro } from "../state/scroll";
import { showVeilInstant, scheduleVeilFadeOut } from "./PhaseTransitionOverlay";

gsap.registerPlugin(ScrollTrigger);

export const PLOT_DWELL_MS = 5000;

export default function SmoothScroll() {
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    const lenis = new Lenis({
      duration: 1.25,
      easing: (t) => 1 - Math.pow(1 - t, 3),
      smoothWheel: true,
      wheelMultiplier: 1.6,
      touchMultiplier: 1.8,
    });

    window.__lenis = lenis;

    // Track blockScroll at effect scope so cleanup can always remove it
    // even if the user navigates away mid-timeout.
    let blockScroll = null;
    let blockScrollTimer = null;

    // CRITICAL: `hasSeenIntro()` is the source of truth for "cold load vs
    // return visit", NOT scrollState.phase. HomePage flips phase to PLOT
    // inside its mount effect, but child effects (this one) run BEFORE
    // parent effects, so we'd still see phase === "INTRO" here on a return
    // SPA visit. Reading hasSeenIntro() directly avoids the race.
    const coldIntro = !hasSeenIntro();

    if (coldIntro) {
      lenis.stop();
      document.documentElement.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
      window.scrollTo(0, 0);
      setScrollProgress(0, 0);
    } else {
      // Return SPA visit — phase will become PLOT inside HomePage's effect
      // a microtask later. Make sure scroll is unlocked and Lenis is running
      // BEFORE that happens, so the user can scroll immediately.
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      lenis.start();
      setTimeout(() => {
        try { ScrollTrigger.refresh(); } catch (_) {}
      }, 80);
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("plot-dwell-end"));
      }, 0);
    }

    const onScroll = () => ScrollTrigger.update();
    lenis.on("scroll", onScroll);

    const tick = (time) => lenis.raf(time * 1000);
    gsap.ticker.add(tick);
    gsap.ticker.lagSmoothing(0);

    const trigger = ScrollTrigger.create({
      start: 0,
      end: () => document.documentElement.scrollHeight - window.innerHeight,
      scrub: true,
      onUpdate: (self) => {
        if (scrollState.phase === "INTRO") return;
        setScrollProgress(self.progress, self.getVelocity());
      },
      onRefresh: () => setScrollProgress(scrollState.progress),
    });

    scrollState.ready = true;

    const unlockScroll = () => {
      // Always remove blockScroll first — prevent stale listeners
      if (blockScroll) {
        window.removeEventListener("wheel",     blockScroll);
        window.removeEventListener("touchmove", blockScroll);
        blockScroll = null;
      }
      if (blockScrollTimer) {
        clearTimeout(blockScrollTimer);
        blockScrollTimer = null;
      }
      try { document.documentElement.style.overflow = ""; } catch (_) {}
      try { document.body.style.overflow = ""; } catch (_) {}
      try { lenis.start(); } catch (_) {}
      try { ScrollTrigger.refresh(); } catch (_) {}
    };

    let lastPhase = scrollState.phase;
    const offPhase = onPhaseChange((cur) => {
      if (cur === lastPhase) return;

      if (cur === "MAP") {
        unlockScroll();
        setTimeout(() => {
          if (scrollState.phase === "MAP") unlockScroll();
        }, 1200);
      }

      if (lastPhase === "INTRO" && cur !== "INTRO") {
        unlockScroll();
      }

      // Direct INTRO -> PLOT (return SPA visit skips the MAP + JOURNEY phases).
      if (lastPhase !== "PLOT" && cur === "PLOT" && hasSeenIntro()) {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        try { lenis.scrollTo(0, { immediate: true }); } catch (_) {}
        setScrollProgress(0, 0);
        unlockScroll();
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            try { ScrollTrigger.refresh(); } catch (_) {}
            window.dispatchEvent(new CustomEvent("plot-dwell-end"));
          });
        });
        lastPhase = cur;
        return;
      }

      // MAP → JOURNEY: reset scroll so the journey starts at 0.
      if (lastPhase === "MAP" && cur === "JOURNEY") {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        try { lenis.scrollTo(0, { immediate: true }); } catch (_) {}
        setScrollProgress(0, 0);
        ScrollTrigger.refresh();
        unlockScroll();
      }

      // JOURNEY → PLOT: reset scroll then let user scroll freely into
      // HomeShowcase → ConstructionPhase → ReservationPhase. No dwell lock.
      // Double-rAF defers ScrollTrigger.refresh() until React has rendered
      // the new sections into the DOM — avoids stale trigger bounds.
      if (lastPhase === "JOURNEY" && cur === "PLOT") {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        try { lenis.scrollTo(0, { immediate: true }); } catch (_) {}
        setScrollProgress(0, 0);
        unlockScroll();
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            try { ScrollTrigger.refresh(); } catch (_) {}
            window.dispatchEvent(new CustomEvent("plot-dwell-end"));
          });
        });
      }

      // MAP → PLOT direct: Journey (the 4-chapter cinematic) was built,
      // then cut as a product decision — Map now hands off straight to
      // PLOT. This used to be a rarely-exercised edge-case fallback, so it
      // still had the synchronous ScrollTrigger.refresh() that caused the
      // MAP->JOURNEY skip bug (refresh() called before React has actually
      // rendered PLOT's sections into the DOM can fire a stale progress
      // value against the old, pre-reset scroll ratio). Now that this is
      // the primary path, it gets the same double-rAF-deferred refresh
      // already proven safe in the JOURNEY->PLOT handler above.
      if (lastPhase === "MAP" && cur === "PLOT") {
        showVeilInstant();
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        try { lenis.scrollTo(0, { immediate: true }); } catch (_) {}
        // A fast touch flick that triggers this handoff still has a large
        // pending inertia scroll queued in Lenis (touchend replays
        // velocity * touchInertiaMultiplier ≈ ×35). If it lands after the
        // reset, PLOT starts already scrolling hard and blows straight
        // through the Construction frame-scrub + Day sections — the
        // "3rd section gets parts skipped" report. lenis.stop() zeroes
        // velocity/target (its reset()) AND swallows further scroll input
        // while stopped, so we hold PLOT still for a beat to absorb that
        // leftover inertia, then start() fresh at the top.
        try { lenis.stop(); } catch (_) {}
        setScrollProgress(0, 0);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            try { ScrollTrigger.refresh(); } catch (_) {}
            window.dispatchEvent(new CustomEvent("plot-dwell-end"));
            scheduleVeilFadeOut();
          });
        });
        // Release the hold once the flick's inertia window has passed, then
        // fully unlock and re-sync triggers at the true top-of-PLOT.
        setTimeout(() => {
          try { lenis.scrollTo(0, { immediate: true }); } catch (_) {}
          try { lenis.start(); } catch (_) {}
          unlockScroll();
          setScrollProgress(0, 0);
          try { ScrollTrigger.refresh(); } catch (_) {}
        }, 420);
      }

      lastPhase = cur;
    });

    return () => {
      offPhase();
      // CRITICAL: always remove blockScroll on unmount.
      // If user navigated away during the 14s window, this prevents
      // the wheel listener from leaking into the next mount.
      if (blockScroll) {
        window.removeEventListener("wheel",     blockScroll);
        window.removeEventListener("touchmove", blockScroll);
        blockScroll = null;
      }
      if (blockScrollTimer) {
        clearTimeout(blockScrollTimer);
        blockScrollTimer = null;
      }
      trigger.kill();
      gsap.ticker.remove(tick);
      lenis.off("scroll", onScroll);
      lenis.destroy();
      delete window.__lenis;
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      scrollState.ready = false;
    };
  }, []);

  return null;
}
